import React, { useState, useEffect } from 'react';

import {
  Stack,
  IconButton,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormHelperText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';

export default function DatasetField({
  index,
  value,
  error,
  setValue,
  setError,
  remove,
}) {
  const [isPublic, setIsPublic] = useState(
    value ? value.type === 'public' : false,
  );
  const [datasetUuid, setDatasetUuid] = useState(
    value ? value.dataset_uuid : '',
  );

  const datasets = useSelector((state) => state.datasets);
  const publicDatasets = useSelector((state) => state.publicDatasets);

  useEffect(() => {
    setDatasetUuid(value?.dataset_uuid || '');
    setIsPublic(value?.type === 'public' || false);
  }, [value]);

  useEffect(() => {
    if (!datasetUuid) return;

    const activeList = isPublic ? (publicDatasets || []) : (datasets || []);
    const exists = activeList.some((d) => d.dataset_uuid === datasetUuid);

    if (!exists) {
      setError(index, 'Dataset missing or deleted');
      return;
    }

    if (error === 'Dataset missing or deleted') {
      setError(index, false);
    }
  }, [datasetUuid, isPublic, datasets, publicDatasets, index, setError, error]);

  const handleChange = (event, val) => {
    const newUuid = val ? val.props.value : event.target.value;
    setDatasetUuid(newUuid);

    // Find the selected dataset to get its name and size
    const selectedDataset = isPublic
      ? publicDatasets?.find((d) => d.dataset_uuid === newUuid)
      : datasets?.find((d) => d.dataset_uuid === newUuid);

    if (selectedDataset) {
      setValue(index, {
        type: isPublic ? 'public' : 'existing',
        dataset_uuid: newUuid,
        name: selectedDataset.name,
        size: selectedDataset.used_size || selectedDataset.size,
      });
      setError(index, false);
    } else {
      setValue(index, {
        type: isPublic ? 'public' : 'existing',
        dataset_uuid: newUuid,
      });
      if (newUuid === '') {
        setError(index, 'Dataset cannot be blank');
      } else {
        setError(index, 'Dataset missing or deleted');
      }
    }
  };

  const existingDatasets = datasets
    ? datasets.filter((dataset) => dataset.status === 'ready')
    : [];

  return (
    <Stack
      direction='row'
      justifyContent='flex-start'
      alignItems='center'
      spacing={1}
    >
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={isPublic}
              onChange={() => {
                setIsPublic(!isPublic);
                setDatasetUuid('');
                setValue(index, {
                  type: !isPublic ? 'public' : 'existing',
                  dataset_uuid: '',
                });
                setError(index, 'Dataset cannot be blank');
              }}
            />
          }
          label='Public'
        />
      </Box>
      <Box sx={{ minWidth: '300px' }}>
        <FormControl fullWidth required>
          <InputLabel id={`dataset-${index}-label`}>Dataset</InputLabel>
          <Select
            labelId={`dataset-${index}-label`}
            id={`dataset-${index}`}
            value={datasetUuid}
            label='Dataset'
            onChange={handleChange}
            aria-describedby={`dataset-${index}-helper-text`}
          >
            {isPublic
              ? publicDatasets?.map((dataset) => {
                  return (
                    <MenuItem
                      key={dataset.dataset_uuid}
                      value={dataset.dataset_uuid}
                      name={dataset.name}
                    >
                      {dataset.name}
                    </MenuItem>
                  );
                })
              : existingDatasets.map((dataset) => {
                  return (
                    <MenuItem
                      key={dataset.dataset_uuid}
                      value={dataset.dataset_uuid}
                      name={dataset.name}
                    >
                      {dataset.name}
                    </MenuItem>
                  );
                })}
          </Select>
          {error ? (
            <FormHelperText id={`dataset-${index}-helper-text`}>
              {error}
            </FormHelperText>
          ) : undefined}
        </FormControl>
      </Box>
      <IconButton
        onClick={() => {
          remove(index);
        }}
      >
        <DeleteIcon />
      </IconButton>
    </Stack>
  );
}
